import angular, { IPromise, IServiceProvider } from "angular";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../records/server/serverUtility";
import {
  ProcessGroup,
  ProcessGroupsResponse,
  ProcessInformation,
} from "../model/ProcessGroups";
import {
  UserProfile,
  vsName as UserProfileName,
} from "../../application/UserProfile";
import { ProcessGroupConfig } from "../config";
import { ServiceResponse } from "../../records/server/query";
import { ProcessApiResponse } from "../model/ProcessApi";
import { StateService } from "@uirouter/angularjs";
import {
  GridSettingsService,
  IGridSettings,
  vsName as GridSettingsServiceName,
} from "../../records/grids/settings/gridSettingsService";
import { ColDef } from "../../records/grids/columns/colDef";

export const vsServiceName = "processInformationService";
export const vsProviderName = `${vsServiceName}Provider`;

interface ProcessGroupRuntime extends ProcessGroup {
  groupConfig?: ProcessGroupConfig;
  gridSettingId: symbol;
}

export class ProcessInformationServiceProvider implements IServiceProvider {
  private runtimeGroupConfigs: {
    [name: string]: ProcessGroupConfig | undefined;
  } = {};

  addRuntimeConfig(groupConfig: ProcessGroupConfig) {
    this.runtimeGroupConfigs[groupConfig.processGroupName] = groupConfig;
  }

  $get = [
    ServerUtilityName,
    UserProfileName,
    "$state",
    "$injector",
    GridSettingsServiceName,
    (
      serverUtility: ServerUtility,
      userProfile: UserProfile,
      $state: StateService,
      $injector: angular.auto.IInjectorService,
      gridSettingsService: GridSettingsService
    ) => {
      const processInformationPromise = serverUtility
        .sendApiRequest<ProcessGroupsResponse>({
          method: "get",
          url: `api/processes/groups`,
        })
        .then((r) => {
          const processGroups = r.ProcessGroups;
          const runtimeGroups = processGroups.map(
            (g) => <ProcessGroupRuntime>g
          );
          runtimeGroups.forEach((pg) => {
            const runtimeGroup = <ProcessGroupRuntime>pg;
            const groupConfig = (runtimeGroup.groupConfig =
              this.runtimeGroupConfigs[runtimeGroup.Name]);
            if (!groupConfig) {
              throw new Error("Could not find process group configuration");
            }
            runtimeGroup.gridSettingId = Symbol(groupConfig.baseSref);
          });
          return runtimeGroups;
        });

      return new ProcessInformationService(
        processInformationPromise,
        userProfile,
        $state,
        serverUtility,
        $injector,
        gridSettingsService
      );
    },
  ];
}

export class ProcessInformationService {
  constructor(
    private readonly processGroupsPromise: IPromise<ProcessGroupRuntime[]>,
    private readonly userProfile: UserProfile,
    private readonly $state: StateService,
    private readonly serverUtility: ServerUtility,
    private readonly $injector: angular.auto.IInjectorService,
    private readonly gridSettingsService: GridSettingsService
  ) {}

  static defaultAdditionalGridColumns = [() => []];
  static defaultDefaultQuery = [() => undefined];

  getProcessGroups() {
    return this.processGroupsPromise;
  }

  getProcessGroup(name: string) {
    return this.processGroupsPromise.then((groups) =>
      groups.find((group) => group.Name.toLowerCase() === name.toLowerCase())
    );
  }

  getAuthorizedGetProcessGroups() {
    return this.processGroupsPromise.then((processGroups) =>
      processGroups.filter((pg) => this.hasRole(pg.GetRole))
    );
  }

  getAuthorizedCreateProcessGroups() {
    return this.processGroupsPromise.then((processGroups) =>
      processGroups.filter((pg) => this.hasRole(pg.CreateRole))
    );
  }

  createProcess(processInformation: ProcessInformation, createDto: any) {
    return this.serverUtility
      .sendApiRequest<ServiceResponse<ProcessApiResponse<any>>>({
        method: "post",
        url: `/api${processInformation.Url}`,
        data: { Data: createDto },
      })
      .then((r) => r.Result);
  }

  getCreateDto(
    processGroup: ProcessGroup,
    process: ProcessInformation,
    dataObject: any
  ) {
    const groupConfig = (<ProcessGroupRuntime>processGroup).groupConfig!;
    return this.$injector.invoke(groupConfig.getCreateProcessDto, null, {
      processGroup,
      process,
      dataObject,
    });
  }

  selectDataObject(processGroup: ProcessGroup, process: ProcessInformation) {
    const groupConfig = (<ProcessGroupRuntime>processGroup).groupConfig!;
    return this.$injector.invoke(groupConfig.onSelectDataObject, null, {
      processGroup,
      process,
    });
  }

  navigateToDetail(processResponse: ProcessApiResponse<any>) {
    const group = processResponse.GroupName;
    const processInstancesId = processResponse.Id;

    return this.getProcessGroup(group).then((g) =>
      this.$state.go(`${g!.groupConfig!.baseSref}.detail`, {
        processInstancesId,
      })
    );
  }

  getGridSettings(processGroup: ProcessGroup): IGridSettings {
    return this.gridSettingsService.getGridSettings(
      (<ProcessGroupRuntime>processGroup).gridSettingId
    );
  }

  getAdditionalGridColumns(processGroup: ProcessGroup): ColDef[] {
    const groupConfig = (<ProcessGroupRuntime>processGroup).groupConfig!;
    return this.$injector.invoke(
      groupConfig.additionalGridColumns ??
        ProcessInformationService.defaultAdditionalGridColumns,
      null,
      {
        processGroup,
      }
    );
  }

  static createNewProcessFlow(
    processInformationService: ProcessInformationService,
    processGroup: ProcessGroup,
    process: ProcessInformation
  ) {
    return processInformationService
      .selectDataObject(processGroup, process)
      .then((dataObject) =>
        processInformationService.getCreateDto(
          processGroup,
          process,
          dataObject
        )
      )
      .then((createDto) =>
        processInformationService.createProcess(process, createDto)
      )
      .then((processResponse) =>
        processInformationService.navigateToDetail(processResponse)
      );
  }

  private hasRole(role: string) {
    return !role || this.userProfile.hasRole(role);
  }
}
